// packages/console/src/hooks/useLivePresets.ts
//
// Loads the workspace's LIVE presets (mode==="live") from FT-3 and owns the
// selected-preset state the Rendition panel edits. patchActive PATCHes the
// selected live preset through the orchestrator and applies the server's echoed
// result optimistically so the Rendition controls feel live.
//
// A file preset (mode absent or "file") is filtered OUT here: the live console
// only lists and edits live presets. With no live preset yet, `active` is null
// and the Rendition panel renders its honest empty state (R10).

import { useCallback, useEffect, useMemo, useState } from "react";
import { listPresets, patchPreset, type Preset, type PresetPatch } from "../api/jobs";

export interface LivePresetsState {
  presets: Preset[];
  active: Preset | null;
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  patchActive: (patch: PresetPatch) => void;
  loading: boolean;
  errorMessage: string | null;
  reload: () => void;
}

// isLive reads a preset's console partition: mode==="live" is a live preset;
// anything else (including the absent legacy default) is a file preset.
function isLive(p: Preset): boolean {
  return p.mode === "live";
}

export function useLivePresets(): LivePresetsState {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setError] = useState<string | null>(null);
  const [reloadTick, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    listPresets(ctrl.signal)
      .then((res) => {
        if (!alive) return;
        setPresets(res.presets.filter(isLive));
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : "failed to load live presets");
        setLoading(false);
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [reloadTick]);

  // Default the selection to the first live preset once loaded; re-snap when the
  // current pick leaves the list (e.g. after a reload).
  useEffect(() => {
    const stillPresent = activeId !== null && presets.some((p) => p.id === activeId);
    if (!stillPresent) setActiveId(presets.length > 0 ? presets[0].id : null);
  }, [activeId, presets]);

  const active = useMemo(() => presets.find((p) => p.id === activeId) ?? null, [presets, activeId]);

  const patchActive = useCallback(
    (patch: PresetPatch) => {
      const id = activeId;
      if (!id) return;
      // Apply the patch LOCALLY first so the Rendition controls move the instant
      // the operator drags a slider or taps a chip, capturing the prior preset to
      // roll back to. This is what makes the panel usable while the live control
      // plane is still coming up: a rejected PATCH restores the prior value and
      // surfaces the error rather than leaving a frozen control.
      let prior: Preset | undefined;
      setPresets((cur) =>
        cur.map((p) => {
          if (p.id !== id) return p;
          prior = p;
          return { ...p, ...patch };
        }),
      );
      (async () => {
        try {
          const updated = await patchPreset(id, patch);
          // Reconcile to the server's echoed preset (it may normalise or clamp).
          setPresets((cur) => cur.map((p) => (p.id === id ? updated : p)));
        } catch (err) {
          // Roll the optimistic edit back to the captured prior value.
          if (prior) setPresets((cur) => cur.map((p) => (p.id === id ? (prior as Preset) : p)));
          setError(err instanceof Error ? err.message : "failed to save live preset");
        }
      })();
    },
    [activeId],
  );

  const reload = useCallback(() => setReload((t) => t + 1), []);

  return { presets, active, activeId, setActiveId, patchActive, loading, errorMessage, reload };
}
